import { Delete as DeleteIcon, InfoOutlined } from "@mui/icons-material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Popper,
  TextField,
} from "@mui/material";
import {
  RowData,
  Table,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Box from "@mui/system/Box/Box";

import { useTranslation } from "@/lib/i18n";
import AddUserDialog from "@/features/admin/features/users/dialogs/add-user";
import {
  UserStatusFilter,
  countUsersByStatus,
  filterUsersByStatus,
} from "@/features/admin/features/users/filter-users-by-status";
import {
  countStatusSearchMatches,
  StatusSearchCount,
} from "@/features/admin/features/users/count-status-search-matches";
import { searchUsersByEmail } from "@/features/admin/features/users/search-users-by-email";
import { Organization, UserAccount, UserAccountStatusEnum } from "@/lib/api";
import {
  useDeleteUserMutation,
  useUsersByOrganisationQuery,
} from "@/features/admin/features/users/hooks/queries";
import { Pagination } from "@/features/admin/features/users/components/pagination";
import { confirm } from "@/features/shared/components";

import {
  LetterCircle,
  Status,
  StyledTable,
  Popup,
  ThIconButton,
  DataStatus,
  TooltipWrapper,
  StyledH2,
  SearchField,
  FilterPills,
} from "./styled";

const USER_TABLE_PAGE_SIZE = 50;

const formatStatusPillCount = (
  { matched, total }: StatusSearchCount,
  isSearchActive: boolean,
): string => (isSearchActive ? `${matched}/${total}` : String(total));

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends RowData, TValue> {
    // options not used but makes eslint happy
    options?: [TData, TValue];
    tooltip?: string | JSX.Element;
  }
}

interface UserTableProps {
  organisation: Organization;
}

const UserTable = ({ organisation }: UserTableProps) => {
  const { t } = useTranslation();
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>("all");
  const [openTooltip, setOpenTooltip] = useState(false);
  const [tooltipAnchorEl, setTooltipAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | undefined>(undefined);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<
    { left: number; top: number } | undefined
  >(undefined);

  const usersQuery = useUsersByOrganisationQuery(organisation.id);
  const deleteUserMutation = useDeleteUserMutation(organisation.id);

  const allUsers = usersQuery.data ?? [];
  const statusCounts = useMemo(() => countUsersByStatus(allUsers), [allUsers]);
  const isSearchActive = searchQuery.trim().length > 0;
  const statusSearchCounts = useMemo(
    () => countStatusSearchMatches(allUsers, searchQuery),
    [allUsers, searchQuery],
  );
  const statusFilteredUsers = useMemo(
    () => filterUsersByStatus(allUsers, statusFilter),
    [allUsers, statusFilter],
  );
  const displayedUsers = useMemo(
    () => searchUsersByEmail(statusFilteredUsers, searchQuery),
    [statusFilteredUsers, searchQuery],
  );
  const isStatusFilterActive = statusFilter !== "all";
  const totalUserCount = allUsers.length;
  const statusFilteredUserCount = statusFilteredUsers.length;
  const displayedUserCount = displayedUsers.length;

  const handleStatusFilterClick = useCallback(
    (nextFilter: Exclude<UserStatusFilter, "all">) => {
      setStatusFilter((currentFilter) =>
        currentFilter === nextFilter ? "all" : nextFilter,
      );
    },
    [],
  );

  useEffect(() => {
    if (statusFilter === "unknown" && statusCounts.unknown === 0) {
      setStatusFilter("all");
    }
  }, [statusFilter, statusCounts.unknown]);

  const handleMenuClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
      if (!event.currentTarget) {
        setMenuAnchorPosition(undefined);
        return;
      }

      const position = {
        left: event.currentTarget.offsetLeft - window.scrollX,
        top: event.currentTarget.offsetTop - window.scrollY,
      };
      setMenuAnchorPosition(position);
      setOpenMenuId(id);
    },
    [setOpenMenuId, setMenuAnchorPosition],
  );

  const handleDeleteClick = useCallback(async () => {
    const confirmed = await confirm(
      t("admin.users.delete_user.title"),
      t("admin.users.delete_user.description", { organisation: organisation.name }),
    );

    if (confirmed && openMenuId) {
      deleteUserMutation.mutate(openMenuId, {
        onSettled: () => {
          setOpenMenuId(undefined);
        },
      });
    } else {
      setOpenMenuId(undefined);
    }
  }, [openMenuId, organisation, deleteUserMutation]);

  const handleTooltipClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!event.target) return;

      setTooltipAnchorEl(event.target as HTMLButtonElement);
      setOpenTooltip((previousOpen) => !previousOpen);
    },
    [setOpenTooltip, setTooltipAnchorEl],
  );

  const columnHelper = createColumnHelper<UserAccount>();
  const columns = [
    columnHelper.accessor("email", {
      id: "firstChar",
      header: () => <></>,
      cell: (info) => (
        <LetterCircle>{info.getValue().charAt(0).toUpperCase()}</LetterCircle>
      ),
    }),
    columnHelper.accessor("email", {
      header: () => "E-mail",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("status", {
      header: () => <span>Status</span>,
      meta: {
        tooltip: (
          <div>
            <strong>Actief</strong>
            <div>
              De gebruiker heeft het tijdelijke wachtwoord aangepast en heeft succesvol
              ingelogd.
            </div>
            <br />
            <strong>Uitgenodigd</strong>
            <div>
              De gebruiker heeft een email ontvangen met een tijdelijk wachtwoord. Dit
              wachtwoord moet bij de eerste keer inloggen worden aangepast. Deze gebruiker
              heeft nog niet ingelogd.
            </div>
          </div>
        ),
      },
      cell: (info) => (
        <Status active={info.getValue() === UserAccountStatusEnum.Active}>
          {t(`admin.users.user.status.${info.getValue() as UserAccountStatusEnum}`)}
        </Status>
      ),
    }),
    columnHelper.accessor("id", {
      id: "actions",
      maxSize: 60,
      header: () => <></>,
      cell: (info) => (
        <IconButton onClick={(e) => handleMenuClick(e, info.getValue() as string)}>
          <MoreVertIcon />
        </IconButton>
      ),
    }),
  ];

  const table = useReactTable({
    data: displayedUsers,
    columns,
    initialState: {
      pagination: {
        pageSize: USER_TABLE_PAGE_SIZE,
        pageIndex: 0,
      },
      sorting: [
        {
          id: "email",
          desc: false,
        },
      ],
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: true,
  });

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <StyledH2>
          {isSearchActive
            ? t("admin.users.overview.title_filtered", {
                filtered: displayedUserCount,
                total: isStatusFilterActive ? statusFilteredUserCount : totalUserCount,
                organisation: organisation.name,
              })
            : t("admin.users.overview.title", {
                number: displayedUserCount || "",
                organisation: organisation.name,
              })}
        </StyledH2>
        <Button
          variant="rounded"
          color="success"
          size="extraLarge"
          endIcon={<PersonAddIcon />}
          onClick={() => setOpenAddUserDialog(true)}
        >
          {t("admin.users.add_user.title")}
        </Button>
      </Box>

      {usersQuery.isError ? (
        <DataStatus>{t("admin.users.overview.error")}</DataStatus>
      ) : null}

      {usersQuery.isLoading ? (
        <DataStatus>
          <CircularProgress />
          {t("admin.users.overview.loading")}
        </DataStatus>
      ) : null}

      {usersQuery.isSuccess ? (
        <FilterPills>
          <Chip
            label={t("admin.users.overview.filters.active", {
              value: formatStatusPillCount(statusSearchCounts.active, isSearchActive),
            })}
            color={statusFilter === "active" ? "primary" : "default"}
            onClick={() => handleStatusFilterClick("active")}
            variant={statusFilter === "active" ? "filled" : "outlined"}
          />
          <Chip
            label={t("admin.users.overview.filters.inactive", {
              value: formatStatusPillCount(statusSearchCounts.inactive, isSearchActive),
            })}
            color={statusFilter === "invited" ? "primary" : "default"}
            onClick={() => handleStatusFilterClick("invited")}
            variant={statusFilter === "invited" ? "filled" : "outlined"}
          />
          {statusCounts.unknown > 0 ? (
            <Chip
              label={t("admin.users.overview.filters.unknown", {
                value: formatStatusPillCount(statusSearchCounts.unknown, isSearchActive),
              })}
              color={statusFilter === "unknown" ? "primary" : "default"}
              onClick={() => handleStatusFilterClick("unknown")}
              variant={statusFilter === "unknown" ? "filled" : "outlined"}
            />
          ) : null}
        </FilterPills>
      ) : null}

      {usersQuery.isSuccess ? (
        <SearchField>
          <TextField
            fullWidth
            size="small"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t("admin.users.overview.search.placeholder")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </SearchField>
      ) : null}

      {usersQuery.isSuccess && isSearchActive && displayedUserCount === 0 ? (
        <DataStatus>
          {t("admin.users.overview.search.no_results", { query: searchQuery.trim() })}
        </DataStatus>
      ) : null}

      {usersQuery.isSuccess && displayedUserCount > 0 ? (
        <StyledTable>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    <TooltipWrapper>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.columnDef.meta?.tooltip && (
                        <>
                          <ThIconButton onClick={handleTooltipClick}>
                            <InfoOutlined />
                          </ThIconButton>
                          <Popper
                            id="mouse-over-popover"
                            open={openTooltip}
                            anchorEl={tooltipAnchorEl}
                            sx={{ maxWidth: "400px", fontSize: "14px" }}
                          >
                            <Popup>{header.column.columnDef.meta.tooltip}</Popup>
                          </Popper>
                        </>
                      )}
                    </TooltipWrapper>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </StyledTable>
      ) : null}

      {usersQuery.isSuccess && displayedUserCount > 0 && (
        <Pagination table={table as Table<unknown>} />
      )}

      <Menu
        open={!!openMenuId}
        onClose={() => setOpenMenuId(undefined)}
        anchorPosition={menuAnchorPosition}
        anchorReference="anchorPosition"
      >
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          Gebruiker verwijderen
        </MenuItem>
      </Menu>

      <AddUserDialog
        organisationId={organisation.id}
        open={openAddUserDialog}
        onClose={() => setOpenAddUserDialog(false)}
      />
    </>
  );
};

export default UserTable;
